// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { MainPanel, DashboardPanel } from "types";
import { IAction } from "actions";
import { IState } from "states/states";
import { PowerViewMode } from "states/cachedstates";
import { getPanelSelection } from "guiutils";
import { EnergySource, IEnergyMeter, ITariffCircuit } from "parameter";
import { isSet } from "utils";

import PowerHeading from "view/comp/PowerHeading";
import TariffCircuitList from "view/comp/TariffCircuitList";
import TariffCircuitListModel from "model/tariffcircuitlist";
import PowerPieChart from "view/comp/PowerPieChart";
import SankeyChart from "view/comp/SankeyChart";
import HierarchyChart from "view/comp/HierarchyChart";

interface IPowerExtProps {
  panel?: DashboardPanel;
}

interface IPowerProps {
  // tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selectedEnergySource: EnergySource | undefined;
  selectedHierarchyId: number | null | undefined;
  powerViewMode: PowerViewMode;
  tariffCircuits: ITariffCircuit[];
}

const mapStateToProps = (state: IState, ownProps: IPowerExtProps) => {
  const { index, selection } = getPanelSelection(state, ownProps.panel ?? DashboardPanel.POWER);

  const props: IPowerProps = {
    energyMeters: state.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
    selectedHierarchyId: selection?.hierarchyId,
    powerViewMode: selection?.powerViewMode ?? PowerViewMode.TABLE,
    tariffCircuits: state.parameter?.tariffline ?? [],
  };
  return props;
};

interface IPowerState {
  filterText: string;
}

interface IPowerDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IPowerDispatch = {};
  return dispatchProps;
};

class PowerComponent extends React.Component<
  IPowerProps & IPowerExtProps & IPowerDispatch,
  IPowerState
> {
  public state: IPowerState = {
    filterText: "",
  };

  public render() {
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    const viewModes = [PowerViewMode.TABLE, PowerViewMode.HIERARCY, PowerViewMode.SANKEY];
    // get all possible EnergySources (allowAllSources = true)
    const energySources = tcModel.getEnergySources(true);
    // check if less or equal than 2 energysources (undefined is alwasy in the list -> plus one ==> one EnergySource)
    if (isSet(this.props.selectedEnergySource) || energySources.length <= 2) {
      viewModes.push(PowerViewMode.PIE);
    }

    return (
      <div id="power">
        <PowerHeading
          viewModes={viewModes}
          panel={this.props.panel}
          filterText={this.state.filterText}
          onFilterTextChanged={(text) => this.setState({ filterText: text })}
        />
        {this.props.powerViewMode === PowerViewMode.TABLE && (
          <TariffCircuitList panel={this.props.panel} filterText={this.state.filterText} />
        )}
        {this.props.powerViewMode === PowerViewMode.PIE && (
          <PowerPieChart panel={this.props.panel} filterText={this.state.filterText} />
        )}
        {this.props.powerViewMode === PowerViewMode.SANKEY && (
          <SankeyChart panel={this.props.panel} filterText={this.state.filterText} />
        )}
        {this.props.powerViewMode === PowerViewMode.HIERARCY && (
          <HierarchyChart panel={this.props.panel} filterText={this.state.filterText} />
        )}
        {this.props.panel !== DashboardPanel.ENERGY && this.renderEnergymeterLink()}
      </div>
    );
  }

  private renderEnergymeterLink() {
    return (
      <div className="mt-3">
        <FormattedMessage id="Click here to see the list of energy meters" />:
        <Link to={`../${MainPanel.ENERGYMETERS}`} className="ms-2">
          <FormattedMessage id="Energy meters" />
        </Link>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PowerComponent);
